import React, { useEffect, useState } from 'react'
import { client } from '../../client/client'
import { Psychologist } from '../../types'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { DataGrid, GridColDef } from '@material-ui/data-grid'
import { format } from 'date-fns'
import nlLocale from 'date-fns/locale/nl'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

interface RegisteredActivity {
  id: string
  date: string
  startTime: string
  psychologistId: number
  psychologistName: string
  totalDuration: string
  consultationType: string
}

const RegisteredActivitiesPage: React.FC = () => {
  const [activities, setActivities] = useState<RegisteredActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [psychologists, setPsychologists] = useState<Psychologist[]>([])
  const [selectedPsychologist, setSelectedPsychologist] = useState<number | ''>(
    ''
  )
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [downloading, setDownloading] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const psychologistsResponse = await client.getPsychologistOptions()
        setPsychologists(psychologistsResponse)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true)
        const response = await client.getRegisteredActivities(
          selectedPsychologist || undefined,
          startDate || undefined,
          endDate || undefined,
          pagination.page,
          pagination.pageSize
        )

        const activitiesWithId = response.data.map((activity, index) => ({
          id: `${activity.date}-${activity.psychologistId}-${index}`,
          date: activity.date,
          startTime: activity.startTime,
          psychologistId: activity.psychologistId,
          psychologistName: activity.psychologistName,
          totalDuration: activity.totalDuration,
          consultationType: activity.consultationType
        }))

        setActivities(activitiesWithId)
        setPagination((prev) => ({
          ...prev,
          total: response.total
        }))
      } catch (error) {
        console.error('Error fetching registered activities:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchActivities()
  }, [
    selectedPsychologist,
    startDate,
    endDate,
    pagination.page,
    pagination.pageSize
  ])

  const getFullName = (psychologist: Psychologist) => {
    if (!psychologist.user) {
      return ''
    }
    const { firstName, infix, lastName } = psychologist.user
    return [firstName, infix, lastName].filter(Boolean).join(' ')
  }

  const formatDateTime = (value: string) =>
    format(new Date(value), 'dd-MM-yyyy HH:mm', { locale: nlLocale })

  const sortedPsychologists = [...psychologists].sort((a, b) => {
    const nameA = getFullName(a).toLowerCase()
    const nameB = getFullName(b).toLowerCase()
    return nameA.localeCompare(nameB)
  })

  const columns: GridColDef[] = [
    {
      field: 'psychologistName',
      headerName: 'Psychologist',
      flex: 1,
      sortable: false,
      filterable: false
    },
    {
      field: 'date',
      headerName: 'Date & Time',
      flex: 1,
      sortable: false,
      filterable: false,
      valueFormatter: (params) => formatDateTime(params.value as string)
    },
    {
      field: 'totalDuration',
      headerName: 'Duration (minutes)',
      flex: 1,
      sortable: false,
      filterable: false
    },
    {
      field: 'consultationType',
      headerName: 'Consultation Type',
      flex: 1,
      sortable: false,
      filterable: false
    }
  ]

  const handleClearFilters = () => {
    setSelectedPsychologist('')
    setStartDate('')
    setEndDate('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleDownloadPdf = async () => {
    if (!pagination.total) {
      return
    }
    try {
      setDownloading(true)
      // Fetch every row matching the current filters (not just the visible page).
      const response = await client.getRegisteredActivities(
        selectedPsychologist || undefined,
        startDate || undefined,
        endDate || undefined,
        1,
        pagination.total
      )

      const selected = psychologists.find(
        (p) => p.id === selectedPsychologist
      )
      const psychologistLabel =
        selectedPsychologist === ''
          ? 'All psychologists'
          : selected
          ? getFullName(selected)
          : `#${selectedPsychologist}`
      const periodLabel = `${startDate || 'start'} - ${endDate || 'now'}`
      const generatedAt = format(new Date(), 'dd-MM-yyyy HH:mm', {
        locale: nlLocale
      })

      const doc = new jsPDF({ orientation: 'landscape' })
      doc.setFontSize(16)
      doc.text('Registered Activities', 14, 16)
      doc.setFontSize(10)
      doc.text(`Psychologist: ${psychologistLabel}`, 14, 24)
      doc.text(`Period: ${periodLabel}`, 14, 30)
      doc.text(`Total activities: ${response.total}`, 14, 36)
      doc.text(`Generated: ${generatedAt}`, 14, 42)

      autoTable(doc, {
        startY: 48,
        head: [
          [
            'Psychologist',
            'Date & Time',
            'Duration (minutes)',
            'Consultation Type'
          ]
        ],
        body: response.data.map((activity) => [
          activity.psychologistName,
          formatDateTime(activity.date),
          activity.totalDuration,
          activity.consultationType
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [63, 81, 181] }
      })

      const fileStamp = format(new Date(), 'yyyy-MM-dd_HH-mm', {
        locale: nlLocale
      })
      doc.save(`registered-activities_${fileStamp}.pdf`)
    } catch (error) {
      console.error('Error generating registered activities PDF:', error)
    } finally {
      setDownloading(false)
    }
  }

  if (loading && !activities.length) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Box p={3} style={{ width: '100%', overflowX: 'auto', paddingTop: '5rem' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">Registered Activities</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownloadPdf}
          disabled={downloading || !pagination.total}
        >
          {downloading ? 'Generating...' : 'Download PDF'}
        </Button>
      </Box>
      <Grid container spacing={2} style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Psychologist</InputLabel>
            <Select
              value={selectedPsychologist}
              onChange={(e) =>
                setSelectedPsychologist(e.target.value as number)
              }
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="">
                <em>All Psychologists</em>
              </MenuItem>
              {sortedPsychologists.map((psychologist) => (
                <MenuItem key={psychologist.id} value={psychologist.id}>
                  {getFullName(psychologist)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClearFilters}
            style={{ height: '100%', width: '100%' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
      <div style={{ width: '100%' }}>
        <DataGrid
          rows={activities}
          columns={columns}
          pageSize={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableSelectionOnClick
          paginationMode="server"
          rowCount={pagination.total}
          onPageChange={(newPage) =>
            setPagination((prev) => ({ ...prev, page: newPage + 1 }))
          }
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({ ...prev, pageSize: newPageSize }))
          }
          loading={loading}
          autoHeight
        />
      </div>
    </Box>
  )
}

export default RegisteredActivitiesPage
